import React, { useCallback, useEffect, useState, useMemo } from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Controls,
  MiniMap,
} from 'reactflow';

import ActionNode from './ActionNode';
import BranchNode from './BranchNode';
import CustomEdge from './CustomEdge';
import AddTriggerButton from './AddTriggerButton';

import 'reactflow/dist/style.css';
import './CustomNode.css';

// Constants
const NODE_WIDTH = 250;
const NODE_HEIGHT = 120;
const SMALL_INCREMENT = 20;
const MAX_ITERATIONS = 10;
const POSITION_THRESHOLD = 1;

const nodeTypes = {
  action: ActionNode,
  branch: BranchNode,
  trigger: ActionNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

// Utility functions for overlap detection
const createNodeBounds = (node, width = NODE_WIDTH, height = NODE_HEIGHT) => ({
  left: node.position.x,
  right: node.position.x + width,
  top: node.position.y,
  bottom: node.position.y + height
});

const checkBoundsOverlap = (bounds1, bounds2) => {
  // Use strict inequality to ensure only actual overlaps trigger repositioning
  // No buffer/spacing - only move if truly overlapping
  return !(bounds1.right <= bounds2.left || 
           bounds1.left >= bounds2.right || 
           bounds1.bottom <= bounds2.top || 
           bounds1.top >= bounds2.bottom);
};

// Generate positions in a spiral pattern from closest to farthest
const generateSpiralPositions = (centerX, centerY, maxRadius = 200, increment = 10) => {
  const positions = [];
  
  // Start with the center position
  positions.push({ x: centerX, y: centerY });
  
  // Generate positions in expanding rings
  for (let radius = increment; radius <= maxRadius; radius += increment) {
    // Generate positions around the current radius
    const steps = Math.max(8, Math.floor(radius / increment) * 4); // More steps for larger radius
    
    for (let i = 0; i < steps; i++) {
      const angle = (2 * Math.PI * i) / steps;
      const x = centerX + Math.round(radius * Math.cos(angle));
      const y = centerY + Math.round(radius * Math.sin(angle));
      positions.push({ x, y });
    }
  }
  
  return positions;
};

// JSON parsing utilities
const attemptJSONFix = (jsonString) => {
  return jsonString
    .replace(/(\w+):/g, '"$1":') // Add quotes around property names
    .replace(/'/g, '"'); // Replace single quotes with double quotes
};

const parseNodeConfig = (configJSON) => {
  try {
    return JSON.parse(configJSON);
  } catch (parseError) {
    try {
      const fixedJSON = attemptJSONFix(configJSON);
      return JSON.parse(fixedJSON);
    } catch (fixError) {
      throw new Error(`Invalid JSON format: ${parseError.message}`);
    }
  }
};


// Error reporting utility
const reportErrorToBubble = (errorMessage, errorInfo) => {
  if (window.bubbleInstance) {
    window.bubbleInstance.publishState('error_message', errorMessage);
    window.bubbleInstance.publishState('error_info', errorInfo);
    window.bubbleInstance.triggerEvent('an_error_occurred');
  }
};

// An internal component to easily access the ReactFlow instance
const InnerFlow = ({ bubbleInstance, initialNodes, initialEdges }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [nodeStartPositions, setNodeStartPositions] = useState({});
  const reactFlowInstance = useReactFlow();

  // Helper function to check if two nodes overlap
  const doNodesOverlap = useCallback((node1, node2) => {
    const bounds1 = createNodeBounds(node1);
    const bounds2 = createNodeBounds(node2);
    return checkBoundsOverlap(bounds1, bounds2);
  }, []);

  // Helper function to check if position overlaps with existing nodes
  const checkPositionOverlap = useCallback((position, existingNodes) => {
    const testNode = { position };
    for (const existingNode of existingNodes) {
      if (doNodesOverlap(testNode, { position: existingNode.position })) {
        return true;
      }
    }
    return false;
  }, [doNodesOverlap]);

  // Helper function to find the closest non-overlapping position
  const findNonOverlappingPosition = useCallback((proposedPosition, existingNodes) => {
    // Generate spiral positions from closest to farthest
    const candidatePositions = generateSpiralPositions(
      proposedPosition.x, 
      proposedPosition.y, 
      300, // max search radius
      SMALL_INCREMENT
    );

    // Find the first (closest) position that doesn't overlap
    for (const position of candidatePositions) {
      if (!checkPositionOverlap(position, existingNodes)) {
        return position;
      }
    }

    // Fallback: if no position found within search radius, use minimal offset
    return {
      x: proposedPosition.x + NODE_WIDTH + 10,
      y: proposedPosition.y + NODE_HEIGHT + 10
    };
  }, [checkPositionOverlap]);

  // Helper function to resolve all overlaps in the current node set
  const resolveAllOverlaps = useCallback((nodeList) => {
    const resolvedNodes = [...nodeList];
    let hasChanges = true;
    let iterations = 0;

    while (hasChanges && iterations < MAX_ITERATIONS) {
      hasChanges = false;
      iterations++;

      for (let i = 0; i < resolvedNodes.length; i++) {
        for (let j = i + 1; j < resolvedNodes.length; j++) {
          if (doNodesOverlap(resolvedNodes[i], resolvedNodes[j])) {
            const newPosition = findNonOverlappingPosition(
              resolvedNodes[j].position,
              resolvedNodes.filter((_, index) => index !== j)
            );
            
            resolvedNodes[j] = {
              ...resolvedNodes[j],
              position: newPosition
            };
            
            hasChanges = true;
          }
        }
      }
    }

    return resolvedNodes;
  }, [doNodesOverlap, findNonOverlappingPosition]);

  // --- Global Access to Bubble Instance ---
  useEffect(() => {
    window.bubbleInstance = bubbleInstance;
    return () => {
      delete window.bubbleInstance;
    }
  }, [bubbleInstance]);

  // --- Event Handlers ---
  const onNodeClick = useCallback((event, node) => {
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null); // Deselect edge when node is clicked
    if (window.bubbleInstance) {
      window.bubbleInstance.publishState('clicked_node_id', node.id);
      window.bubbleInstance.triggerEvent('node_clicked');
    }
  }, []);

  // Handle edge click to select/highlight edge
  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null); // Deselect node when edge is clicked
    if (window.bubbleInstance) {
      window.bubbleInstance.publishState('clicked_edge_id', edge.id);
      window.bubbleInstance.triggerEvent('edge_clicked');
    }
  }, []);

  // Handle canvas click to deselect nodes and edges
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, []);

  // Handle Add Trigger button click
  const onAddTrigger = useCallback(() => {
    if (window.bubbleInstance) {
      window.bubbleInstance.triggerEvent('add_trigger_requested');
    }
  }, []);


  // Handle node drag start - track the starting position
  const handleNodeDragStart = useCallback((event, node) => {
    setNodeStartPositions(prev => ({
      ...prev,
      [node.id]: { x: node.position.x, y: node.position.y }
    }));
  }, []);

  // Helper to check if node has moved significantly
  const hasNodeMoved = useCallback((currentPosition, startPosition) => {
    if (!startPosition) return false;
    return Math.abs(currentPosition.x - startPosition.x) > POSITION_THRESHOLD || 
           Math.abs(currentPosition.y - startPosition.y) > POSITION_THRESHOLD;
  }, []);

  // Handle node drag stop - send position data to Bubble only if node actually moved
  const handleNodeDragStop = useCallback((event, node) => {
    if (!window.bubbleInstance) return;
    
    const startPosition = nodeStartPositions[node.id];
    
    if (hasNodeMoved(node.position, startPosition)) {
      window.bubbleInstance.publishState("moved_node_id", node.id);
      window.bubbleInstance.publishState("moved_node_x", node.position.x);
      window.bubbleInstance.publishState("moved_node_y", node.position.y);

      window.bubbleInstance.triggerEvent("NodeMoved");
    }
    
    // Clean up the starting position for this node
    setNodeStartPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[node.id];
      return newPositions;
    });
  }, [nodeStartPositions, hasNodeMoved]);

  // Update nodes with selection state and edge connection highlighting
  const nodesWithSelection = useMemo(() => {
    // Find the selected edge to determine which nodes to highlight
    const selectedEdge = selectedEdgeId ? edges.find(edge => edge.id === selectedEdgeId) : null;
    
    return nodes.map(node => {
      // Check if this node has a branch child
      const hasBranchChild = edges.some(edge => 
        edge.source === node.id && 
        nodes.find(targetNode => targetNode.id === edge.target && targetNode.type === 'branch')
      );
      
      return {
        ...node,
        data: {
          ...node.data,
          isSelected: node.id === selectedNodeId,
          isConnectedToSelectedEdge: selectedEdge && (node.id === selectedEdge.source || node.id === selectedEdge.target),
          hasBranchChild: hasBranchChild
        }
      };
    });
  }, [nodes, selectedNodeId, selectedEdgeId, edges]);

  // Update edges with selection state
  const edgesWithSelection = useMemo(() => {
    return edges.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        isSelected: edge.id === selectedEdgeId
      }
    }));
  }, [edges, selectedEdgeId]);


  // --- Global Functions for Bubble ---
  useEffect(() => {
    window.getReactFlowState = () => {
      if (!reactFlowInstance) return null;
      const flow = reactFlowInstance.toObject();
      return JSON.stringify(flow, null, 2);
    };
    return () => { delete window.getReactFlowState; };
  }, [reactFlowInstance]);

  useEffect(() => {
    window.loadReactFlowState = (jsonString) => {
      if (!jsonString) {
        return;
      }
      
      // If ReactFlow instance isn't ready, queue the data
      if (!reactFlowInstance) {
        window.pendingReactFlowData = jsonString;
        return;
      }
      
      try {
        const flow = JSON.parse(jsonString);
        
        if (flow) {
          const nodes = flow.nodes || [];
          const edges = flow.edges || [];
          
          // Filter out invalid edges (empty source/target)
          const validEdges = edges.filter(edge => {
            const isValid = edge.source && edge.target && edge.source.trim() !== '' && edge.target.trim() !== '';
            return isValid;
          });
          
          // Resolve any overlaps in the loaded nodes
          const resolvedNodes = resolveAllOverlaps(nodes);

          setNodes(resolvedNodes);
          setEdges(validEdges);
          
          // Use requestAnimationFrame to ensure DOM is updated before fitView
          requestAnimationFrame(() => {
            setTimeout(() => {
              reactFlowInstance.fitView({ duration: 200, padding: 0.1 });
            }, 100);
          });
        }
      } catch (e) {
        reportErrorToBubble(e.toString(), 'loadReactFlowState');
      }
    };
    
    // Process any queued data when ReactFlow instance becomes available
    if (reactFlowInstance && window.pendingReactFlowData) {
      window.loadReactFlowState(window.pendingReactFlowData);
      delete window.pendingReactFlowData;
    }
    
    return () => { delete window.loadReactFlowState; };
  }, [reactFlowInstance, setNodes, setEdges, resolveAllOverlaps]);

  // *** NEW: Function to resolve overlaps in current nodes ***
  useEffect(() => {
    window.resolveOverlaps = () => {
      if (!reactFlowInstance) return;
      const currentNodes = reactFlowInstance.getNodes();
      const resolvedNodes = resolveAllOverlaps(currentNodes);
      setNodes(resolvedNodes);
    };

    // Function to zoom to specific coordinates
    window.zoomToPosition = (x, y, zoom = 1.5) => {
      if (!reactFlowInstance) return;
      reactFlowInstance.setCenter(x, y, { zoom, duration: 800 });
    };

    // Function to zoom to specific node by ID
    window.zoomToNode = (nodeId, zoom = 1.5) => {
      if (!reactFlowInstance) return;
      const node = reactFlowInstance.getNode(nodeId);
      if (node) {
        const x = node.position.x + (node.width || 250) / 2;
        const y = node.position.y + (node.height || 120) / 2;
        reactFlowInstance.setCenter(x, y, { zoom, duration: 800 });
      }
    };

    return () => { 
      delete window.resolveOverlaps;
      delete window.zoomToPosition;
      delete window.zoomToNode;
    };
  }, [reactFlowInstance, setNodes, resolveAllOverlaps]);

  
  // Helper to calculate new node position based on source node and handle
  const calculateNewNodePosition = useCallback((sourceNode, sourceHandle) => {
    let xPos = sourceNode.position.x;
    if (sourceNode.type === 'branch') {
      xPos += (sourceHandle === 'true' ? -100 : 100);
    }
    return { x: xPos, y: sourceNode.position.y + 150 };
  }, []);

  // Helper to create new node and edge objects
  const createNodeAndEdge = useCallback((sourceNodeId, newNodeConfig, sourceHandle, finalPosition) => {
    const newNodeId = `node_${new Date().getTime() + 1}`;
    const nodeData = newNodeConfig.data || newNodeConfig;

    const newNode = {
      id: newNodeId,
      type: newNodeConfig.type || 'action',
      position: finalPosition,
      data: nodeData,
    };

    const newEdge = {
      id: `${sourceNodeId}-${newNodeId}`,
      source: sourceNodeId,
      target: newNodeId,
      sourceHandle: sourceHandle || null,
      type: 'custom',
    };

    return { newNode, newEdge, newNodeId };
  }, []);

  useEffect(() => {
    window.addNodeFromBubble = (sourceNodeId, newNodeConfigJSON, sourceHandle) => {
      if (!reactFlowInstance || !sourceNodeId || !newNodeConfigJSON) {
        return;
      }
      
      try {
        const sourceNode = reactFlowInstance.getNode(sourceNodeId);
        if (!sourceNode) { 
          return; 
        }
        
        const newNodeConfig = parseNodeConfig(newNodeConfigJSON);
        
        const proposedPosition = calculateNewNodePosition(sourceNode, sourceHandle);
        const finalPosition = findNonOverlappingPosition(proposedPosition, nodes);
        
        const { newNode, newEdge, newNodeId } = createNodeAndEdge(sourceNodeId, newNodeConfig, sourceHandle, finalPosition);

        setNodes((nodes) => nodes.concat(newNode));
        setEdges((edges) => edges.concat(newEdge));
        
        // Publish the new node ID to Bubble
        if (window.bubbleInstance) {
          window.bubbleInstance.publishState('trigger_node_id', newNodeId);
          window.bubbleInstance.triggerEvent('node_created');
        }
      } catch (e) { 
        const errorMessage = e.message || e.toString();
        reportErrorToBubble(errorMessage, 'addNodeFromBubble');
      }
    };
    return () => { delete window.addNodeFromBubble; };
  }, [reactFlowInstance, setNodes, setEdges, findNonOverlappingPosition, nodes, calculateNewNodePosition, createNodeAndEdge]);

  // Expose error reporting function globally
  useEffect(() => {
    window.reportErrorToBubble = reportErrorToBubble;
    return () => {
      delete window.reportErrorToBubble;
    };
  }, []);


  return (
    <ReactFlow
      nodes={nodesWithSelection}
      edges={edgesWithSelection}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeClick={onNodeClick}
      onEdgeClick={onEdgeClick}
      onNodeDragStart={handleNodeDragStart}
      onNodeDragStop={handleNodeDragStop}
      multiSelectionKeyCode={null}
      selectionKeyCode={null}
      deleteKeyCode={null}
      fitView
      nodesConnectable={false}
      edgesFocusable={true}
      proOptions={{ hideAttribution: true }}
      onPaneClick={onPaneClick}
    >
      <Background variant="dots" gap={12} size={1} color="#e0e0e0" />
      <Controls 
        position="top-left"
        style={{
          background: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          position: 'fixed',
          top: '24px',
          left: '24px',
          margin: '0',
        }}
        showZoom={true}
        showFitView={true}
        showInteractive={false}
      />
      <MiniMap
        position="bottom-left"
        style={{
          background: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          bottom: '24px',
          left: '24px',
          margin: '0',
        }}
        nodeColor={(node) => {
          if (node.type === 'branchNode') return '#f97316';
          if (node.type === 'actionNode') return '#16a34a';
          return '#4f46e5';
        }}
        nodeStrokeWidth={2}
        nodeBorderRadius={6}
        pannable={true}
        zoomable={true}
        maskColor="rgba(0, 0, 0, 0.1)"
        width={200}
        height={150}
      />
      {nodes.length === 0 && (
        <AddTriggerButton 
          hasNodes={false}
          onAddTrigger={onAddTrigger}
        />
      )}
    </ReactFlow>
  );
};

// The main component now handles parsing the initial data
const ReactFlowComponent = ({ bubbleInstance, initialDataJSON }) => {
  let initialNodes = [];
  let initialEdges = [];
  
  const fallbackData = {
    nodes: [],
    edges: []
  };

  try {
    if (initialDataJSON) {
      const initialData = JSON.parse(initialDataJSON);
      initialNodes = initialData.nodes || fallbackData.nodes;
      initialEdges = initialData.edges || fallbackData.edges;
    } else {
        initialNodes = fallbackData.nodes;
        initialEdges = fallbackData.edges;
    }
  } catch(e) {
      reportErrorToBubble(e.toString(), 'Initial Data Parse');
      initialNodes = fallbackData.nodes;
      initialEdges = fallbackData.edges;
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <InnerFlow
          bubbleInstance={bubbleInstance}
          initialNodes={initialNodes}
          initialEdges={initialEdges}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default ReactFlowComponent;